import {
  ButtonColor,
  ButtonSize,
  Combobox,
  Modal,
  ModalType,
  Option,
  Table,
  TableRow,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AddressPageValidation, Customer } from 'src/front/api/apiInstance';
import { ReferenceDataType } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import { convertStringToDate, formatDate } from 'src/front/common/utils/dateTimeUtils';
import {
  dictionaryLineFormat,
  formatAddressData,
  formatAddressSuggestion,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import AsyncButton from 'src/front/components//AsyncButton';

import {
  Container,
  FormElement,
  FormRow,
  Subtitle,
  TableWrapper,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import { BREAK_POINTS } from 'src/front/constant';
import styled from 'styled-components';
import uid from 'uid';

import AddressModal, {
  ACTUAL_ADDRESS_CODE,
  AddressFormData as Address,
  REGISTRATION_ADDRESS_CODE,
} from './AddressModal';

const LineFormElement = styled(FormElement)`
  width: 100%;
  max-width: 900px;

  @media screen and (max-width: ${BREAK_POINTS.MAX.LARGE_1X}) {
    max-width: 600px;
  }
`;

const addressTableHeaders = [
  'cardEntry_addressEntry_addressType_label',
  'cardEntry_addressEntry_title',
  'general_status_label',
];

interface Props {
  customerId?: string;
  goToNextStep: (customerId?: string) => void;
  disablePage?: boolean;
}

const addressDefaultValues = {
  isRegistration: false,
  addressQuery: '',
  manualAddress: true,
  registrationDate: null,
  selectedSuggestion: null,
};

const initialAddressInfo = {
  hasRegistrationAddress: false,
  hasActualAddress: false,
};
const getAddressQuery = (val: string): string => `${val?.trim() ? `${val?.trim()}, ` : ''}`;

const getAddressValue = (address: any, type: string): string => {
  const valueType = type === 'label';
  const streetTypeLabel = valueType ? address.streetType?.label : address.streetType?.value;

  return `${getAddressQuery(address.street)}${streetTypeLabel}${
    address.houseNumber.trim()
      ? ` ${address.houseNumber.trim()}, `
      : `${streetTypeLabel ? ', ' : ''}`
  }
  ${getAddressQuery(address.buildingInfo)}${getAddressQuery(address.flatNumber)}${getAddressQuery(
    address.city
  )}${
    valueType ? getAddressQuery(address.district?.label) : getAddressQuery(address.district?.value)
  }${valueType ? address.region?.label : address.region?.value}, ${getAddressQuery(
    address.zipCode
  )}${valueType ? address.country?.label : address.country?.value}`;
};

const AddressEntry: FC<Props> = ({ customerId, goToNextStep, disablePage }) => {
  const { t } = useTranslation();

  const [showAddressModal, setShowAddressModal] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const [warning, setWarning] = useState<string>();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressInfo, setAddressInfo] = useState(initialAddressInfo);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [addressFormValues, setAddressFormValues] = useState<Address>();
  const [selectedAddress, setSelectedAddress] = useState<TableRow>();
  const [selectedCorrespondence, setSelectedCorrespondence] = useState<Option | null>(null);
  const [hasCorrespondenceError, setHasCorrespondenceError] = useState<boolean>(false);
  const secondAddressId = uid();

  const addressTypes = usePRM(ReferenceDataType.ADDRESSTYPE);
  const selectedAddresses = addresses.map((address) => address.addressType?.value || '');
  const filteredAddresses = addressTypes.filter((type) =>
    addressFormValues
      ? !selectedAddresses.includes(type.value) ||
        addressFormValues?.addressType?.value === type.value
      : !selectedAddresses.includes(type.value)
  );
  const handleSubmit = async (): Promise<void> => {
    const hasAddress = addresses.length > 0;
    if (hasAddress && selectedCorrespondence) {
      setWarning(undefined);
      const { data } = await AddressPageValidation.validateAddressPageData({
        adressList: addresses.map((addr) => {
          const { addressId, selectedSuggestion, ...rest } = addr;
          return {
            ...rest,
            ...(selectedSuggestion
              ? { selectedSuggestion: formatAddressData(selectedSuggestion) }
              : {}),
            addressType: dictionaryLineFormat(addr.addressType),
            registrationDate: formatDate(addr.registrationDate),
            ...(addr.isAddressNew ? {} : { addressId: addr.addressId }),
            country: dictionaryLineFormat(addr.country),
            region: dictionaryLineFormat(addr.region),
            district: dictionaryLineFormat(addr.district),
            streetType: dictionaryLineFormat(addr.streetType),
            hasCorrespondenceFlag: selectedCorrespondence.value === addr.addressId,
          };
        }),
      });

      if (data.result === 'OK') {
        goToNextStep(customerId);
      }
    } else if (!selectedCorrespondence) {
      setHasCorrespondenceError(true);
    } else {
      setWarning('cardEntry_addressEntry_noAddressInfo_errorMessage');
    }
  };

  const updateAddressInfo = (addressList: Address[]): void => {
    const addressInformation = addressList.reduce((acc, addr) => {
      if (addr.addressType?.value === REGISTRATION_ADDRESS_CODE) {
        return { ...acc, hasRegistrationAddress: true };
      }
      if (addr.addressType?.value === ACTUAL_ADDRESS_CODE) {
        return { ...acc, hasActualAddress: true };
      }
      return acc;
    }, initialAddressInfo);
    setAddressInfo(addressInformation);
  };

  const loadAddresses = useCallback(async (): Promise<void> => {
    try {
      setLoadingTable(true);
      const { data: response } = await AddressPageValidation.getAddressPageData();
      if (response.result === 'OK' && response.pageData) {
        const { adressList } = response.pageData;

        const addressList =
          adressList?.map((addr) => ({
            ...addr,
            isAddressNew: addr.isAddressNew || false,
            street: addr.street || '',
            buildingInfo: addr.buildingInfo || '',
            manualAddress: addr.manualAddress || false,
            addressQuery: addr.addressQuery || '',
            houseNumber: addr.houseNumber || '',
            zipCode: addr.zipCode || '',
            flatNumber: addr.flatNumber || '',
            selectedSuggestion: addr.selectedSuggestion
              ? formatAddressSuggestion(addr.selectedSuggestion)
              : null,
            addressId: addr.isAddressNew ? uid() : addr.addressId,
            addressType: optionFormat(addr.addressType),
            registrationDate: convertStringToDate(addr.registrationDate),
            country: optionFormat(addr.country),
            region: optionFormat(addr.region),
            district: addr.district ? optionFormat(addr.district) : null,
            streetType: optionFormat(addr.streetType),
          })) || [];

        const correspondenceAdress = addressList.find((addr) => addr.hasCorrespondenceFlag);
        correspondenceAdress &&
          setSelectedCorrespondence({
            label: `${correspondenceAdress.addressType?.label} - ${getAddressValue(
              correspondenceAdress,
              'label'
            )}`,
            value: correspondenceAdress.addressId || '',
          });
        setAddresses(addressList);
      } else if (customerId) {
        const { data } = await Customer.getCustomerAddress({ customerId });

        const addressList =
          data.customerAddressList?.map((address) => ({
            ...addressDefaultValues,
            addressId: address.addressId,
            addressType: optionFormat(address.addressType),
            country: optionFormat(address.country),
            region: optionFormat(address.region),
            city: address.city,
            district: optionFormat(address.district),
            streetType: optionFormat(address.streetType),
            street: address.street,
            buildingInfo: address.buildingInfo,
            flatNumber: address.flatNumber,
            houseNumber: address.houseNumber,
            zipCode: address.zipCode,
            isAddressNew: address.isAddressNew,
            hasCorrespondenceFlag: address.hasCorrespondenceFlag,
            registrationDate: convertStringToDate(address.registrationDate),
            addressQuery: getAddressValue(address, 'value'),
          })) || [];

        const correspondenceAdress = addressList.find((addr) => addr.hasCorrespondenceFlag);
        correspondenceAdress &&
          setSelectedCorrespondence({
            label: `${correspondenceAdress.addressType?.label} - ${getAddressValue(
              correspondenceAdress,
              'label'
            )}
            `,
            value: correspondenceAdress.addressId || '',
          });
        setAddresses(addressList);
      } else {
        setShowAddressModal(true);
      }
      setLoadingTable(false);
    } catch (error) {
      // TODO (ozgunbal): show error on modal or handle with error boundary
    }
  }, [customerId]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  useEffect(() => {
    updateAddressInfo(addresses);
  }, [addresses]);

  const addAddress = useCallback((): void => {
    setAddressFormValues(undefined);
    setShowAddressModal(true);
  }, []);

  const editAddress = useCallback(
    (item?: TableRow): void => {
      setAddressFormValues(addresses.find((addr) => addr.addressId === item?.id));
      setShowAddressModal(true);
    },
    [addresses]
  );

  const deleteAddress = useCallback((item?: TableRow): void => {
    setIsDeleteModalOpen(true);
  }, []);

  const addressActions = useMemo(
    () => ({
      add: selectedAddresses.length === addressTypes.length ? undefined : addAddress,
      edit: editAddress,
      delete: deleteAddress,
    }),
    [addAddress, addressTypes.length, deleteAddress, editAddress, selectedAddresses.length]
  );

  const closeAddressModal = (): void => setShowAddressModal(false);

  const handleAddressSubmit = async (address: Address, secondAddress?: Address): Promise<void> => {
    let newAddresses;
    if (address?.addressId) {
      newAddresses = addresses.map((addr) =>
        addr.addressId === address.addressId ? address : addr
      );
      if (selectedCorrespondence?.value === address.addressId) {
        setSelectedCorrespondence({
          label: `${address.addressType?.label} - ${getAddressValue(address, 'label')}`,
          value: address.addressId || '',
        });
      }
    } else {
      newAddresses = [
        { ...address, addressId: uid(), isAddressNew: true },
        ...(secondAddress
          ? [{ ...secondAddress, addressId: secondAddressId, isAddressNew: true }]
          : []),
        ...addresses,
      ];
      secondAddress &&
        handleCorrespondenceAddress({
          label: `${secondAddress.addressType?.label} - ${getAddressValue(secondAddress, 'label')}`,
          value: secondAddressId,
        });
    }
    setAddresses(newAddresses);
    setShowAddressModal(false);
    return Promise.resolve();
  };

  const handleDeleteAddress = (): void => {
    setAddresses(addresses.filter((addr) => addr.addressId !== selectedAddress?.id));
    if (selectedCorrespondence?.value === selectedAddress?.id) {
      setSelectedCorrespondence(null);
    }
    setSelectedAddress(undefined);
    setIsDeleteModalOpen(false);
  };

  const handleCorrespondenceAddress = (item: Option | null): void => {
    setSelectedCorrespondence(item);
    hasCorrespondenceError && setHasCorrespondenceError(false);
  };

  const correspondenceAdresses: Option[] = addresses.map((address) => ({
    label: `${address.addressType?.label} - ${getAddressValue(address, 'label')}`,
    value: address.addressId || '',
  }));

  const addressTableData: TableRow[] = addresses.map(
    (address: Address): TableRow => ({
      id: address.addressId || '',
      columns: [
        address.addressType?.label || '',
        getAddressValue(address, 'label'),
        t(address.isAddressNew ? 'general_new_text' : 'general_current_text'),
      ],
      deletable: address.isAddressNew,
    })
  );

  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_addressEntry_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="cardEntry_addressEntry_addressList_text" />
      </Subtitle>
      {isDeleteModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_deleteRecordConfirm_text')}
          onCancel={() => setIsDeleteModalOpen((state) => !state)}
          onSubmit={handleDeleteAddress}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      ) : null}
      {showAddressModal ? (
        <AddressModal
          addressInfo={addressInfo}
          addressTypes={filteredAddresses}
          formValues={addressFormValues}
          onClose={closeAddressModal}
          submit={handleAddressSubmit}
        />
      ) : null}
      <TableWrapper>
        <Table
          actionConfig={addressActions}
          addActionText={t('general_add_button')}
          disabled={disablePage}
          emptyTableText={t('cardEntry_addressEntry_noAddressFound_text')}
          headers={addressTableHeaders.map((head) => t(head))}
          items={addressTableData}
          loading={loadingTable}
          onSelect={setSelectedAddress}
          pageNumber={1}
          selectedItem={selectedAddress}
          widths={['25', '60', '15']}
        />
      </TableWrapper>
      <Subtitle>
        <Trans i18nKey="cardEntry_addressEntry_correspondanceAddress_subtitle" />
      </Subtitle>
      <LineFormElement>
        <Combobox
          disabled={disablePage}
          errorMessage={hasCorrespondenceError ? t('general_requiredField_errorMessage') : ''}
          items={correspondenceAdresses}
          label={t('cardEntry_addressEntry_correspondenceAddress_label')}
          name="correspondence"
          onChange={handleCorrespondenceAddress}
          value={selectedCorrespondence}
          required
        />
      </LineFormElement>
      <WarningText>
        <Trans i18nKey={warning} />
      </WarningText>
      <FormRow>
        <AsyncButton
          color={ButtonColor.Primary}
          onClick={handleSubmit}
          size={ButtonSize.Medium}
          text={t('general_continue_button')}
        />
      </FormRow>
    </Container>
  );
};

export default AddressEntry;
